import {fromEvent, map, skip, Subscription } from "rxjs";
import Logger from "../lib/logger";
import IndexDBService from "../lib/indexDB.service";
import StorageService from "../lib/storage.service";
import { estimate$, trayData$ } from "../lib/state";
import { EstimateData, TrayData } from "../lib/types";
import { getFancySingleDimString, isInIframe, money } from "../lib/util.functions";

class TrayReviewMain extends HTMLElement {
  #NAME = 'TrayReviewMain';
  #subs?: Subscription[];
  #imgWrap: HTMLElement;
  #lineItemsDiv: HTMLElement;
  #addToCartBtn: HTMLButtonElement;
  #buyItNowBtn: HTMLButtonElement;
  #quantityInput: HTMLInputElement;
  #form: HTMLFormElement;
  #inputs: HTMLInputElement[];
  #trayData?: TrayData;

  constructor() {
    super();
    Logger.info(`${this.#NAME} > constructor`);
    this.#imgWrap = this.querySelector('[data-image-wrap]') as HTMLElement;
    this.#lineItemsDiv = this.querySelector('[data-line-items]') as HTMLElement;
    this.#addToCartBtn = this.querySelector('[data-add-to-cart-btn]') as HTMLButtonElement;
    this.#buyItNowBtn = this.querySelector('[data-buy-it-now-btn]') as HTMLButtonElement;
    this.#quantityInput = this.querySelector('number-button input') as HTMLInputElement;
    this.#form = this.querySelector('form') as HTMLFormElement;
    this.#inputs = Array.from(this.querySelectorAll('input'));
    StorageService.remove('buy-it-now-custom');
  }

  connectedCallback(): void {
    Logger.info(`${this.#NAME} > connectedCallback`);
    this.#getImage();
    this.#subs = [
      estimate$.pipe(skip(1)).subscribe((val) => this.#onEstimate(val)),
      trayData$.pipe(skip(1)).subscribe((val) => this.#onTrayData(val)),
      // update quantity
      fromEvent<InputEvent>(this.#quantityInput, 'input').pipe(
        map((e) => (e.target as HTMLInputElement).valueAsNumber),
      ).subscribe((val) => {
        this.#updateTotal(val);
      }),
      // add to cart
      fromEvent<MouseEvent>(this.#addToCartBtn, 'click').subscribe((e) => {
        e.preventDefault();
        this.#buyItNowBtn.disabled = true;
        (this.#addToCartBtn.querySelector('loading-anim') as HTMLElement).dataset.loading = 'true';
        this.#form.submit();
      }),
      // buy it now
      fromEvent<MouseEvent>(this.#buyItNowBtn, 'click').subscribe((e) => {
        e.preventDefault();
        this.#addToCartBtn.disabled = true;
        (this.#buyItNowBtn.querySelector('loading-anim') as HTMLElement).dataset.loading = 'true';
        StorageService.save('buy-it-now-custom', true, true);
        this.#form.submit();
      })
    ];
  }

  disconnectedCallback(): void {
    Logger.info(`${this.#NAME} > disconnectedCallback`);
    this.#subs?.forEach(sub => sub.unsubscribe());
  }

  async #getImage() {
    Logger.info(`${this.#NAME} > #getImage`);
    const indexDBService = new IndexDBService('jt-db');
    await indexDBService.init();
    const file1 = await indexDBService.getFile('custom-tray.png') as File;
    const file2 = await indexDBService.getFile('custom-tray-layout.svg') as File;
    if (file1 && file2) {
      const img = document.createElement('img');
      img.src = URL.createObjectURL(file1);
      img.title = 'custom jewelery tray 3D render';
      this.#imgWrap.appendChild(img);
      const imgInput = this.#inputs.find(input => input.name === 'properties[3D render]');
      if (imgInput) this.#updateImageInput(imgInput, file1);
      const layoutInput = this.#inputs.find(input => input.name === 'properties[layout]');
      if (layoutInput) this.#updateImageInput(layoutInput, file2);
      setTimeout(() => {
        const loadingAnim = this.#imgWrap.querySelector('loading-anim') as HTMLElement;
        if (loadingAnim)  loadingAnim.dataset.loading = 'false';
      }, 20);
    } else if (!isInIframe()) {
      const backlink = this.querySelector('#back-link') as HTMLAnchorElement;
      if (backlink) backlink.click();
    }
    
  }

  #updateImageInput(input: HTMLInputElement, file: File) {
    const dataTransfer = new DataTransfer();
		dataTransfer.items.add(file);
		input.files = dataTransfer.files;
  }

  #onEstimate(estimate: EstimateData) {
    Logger.info(`${this.#NAME} > #onEstimate >`, estimate);
    const lineItems: HTMLDivElement[] = [];
    const refs: string[] = [];
    const quantities: number[] = [];
    const prices: number[] = [];
    //
    lineItems.push(this.#createLineItem(estimate.tray.title, estimate.tray.price));
    //
    estimate.otherCharges.forEach(obj => {
      lineItems.push(this.#createLineItem(obj.title, obj.price * (obj.quantity || 1), true, obj.quantity));
      refs.push(obj.id);
      quantities.push(obj.quantity || 1);
      prices.push(obj.price);
    });
    this.#lineItemsDiv.append(...lineItems);
    //
    const idInput = this.#inputs.find(input => input.name === 'id');
    if (idInput) idInput.value = estimate.tray.id;
    //
    const refInput = this.#inputs.find(input => input.name === 'properties[_component_reference]');
    if (refInput) refInput.value = JSON.stringify(refs);
    //
    const quantitiesInput = this.#inputs.find(input => input.name === 'properties[_component_quantities]');
    if (quantitiesInput) quantitiesInput.value = JSON.stringify(quantities);
    //
    const pricesInput = this.#inputs.find(input => input.name === 'properties[_component_prices]');
    if (pricesInput) pricesInput.value = JSON.stringify(prices);
  }

  #updateTotal(quantity: number) {
    const totalSpan = this.querySelector('span[data-total]');
    if (totalSpan) totalSpan.textContent = money((this.#trayData?.subtotal ?? 0 )* quantity);
  }

  #onTrayData(data: TrayData) {
    Logger.info(`${this.#NAME} > #onTrayData >`, data);
    //
    this.#trayData = data;
    //
    const widthInput = this.#inputs.find(input => input.name === 'properties[width]');
    if (widthInput) widthInput.value = getFancySingleDimString(data.width);
    //
    const depthInput = this.#inputs.find(input => input.name === 'properties[depth]');
    if (depthInput) depthInput.value = getFancySingleDimString(data.depth);
    //
    const heightInput = this.#inputs.find(input => input.name === 'properties[height]');
    if (heightInput) heightInput.value = getFancySingleDimString(data.height);
    //
    this.#updateTotal(1);
    //
    const wSpan  = this.querySelector('span[data-width]');
    if (wSpan) wSpan.textContent = getFancySingleDimString(data.width);
    const dSpan  = this.querySelector('span[data-depth]');
    if (dSpan) dSpan.textContent = getFancySingleDimString(data.depth);
    const hSpan  = this.querySelector('span[data-height]');
    if (hSpan) hSpan.textContent = getFancySingleDimString(data.height);
    const cSpan  = this.querySelector('span[data-color]');
    if (cSpan && data.color) cSpan.textContent = data.color.name;
  }

  #createLineItem(title: string, price: number, extra: boolean = false, quantity?: number ) {
    const div = document.createElement('div');
    div.className = 'custom-tray-review__content__line-items__item';
    div.dataset.title = title;
    const tspan = document.createElement('span');
    tspan.textContent = quantity ? `${title} x ${quantity}` : title;
    const pspan = document.createElement('span');
    pspan.textContent = money(price);
    if (extra) pspan.dataset.extra = 'true';
    div.append(tspan, pspan);
    return div;
  }
}

customElements.define('tray-review-main', TrayReviewMain);